/**
 * Source-specific converters that normalise raw report content strings into structured objects.
 * Each converter must produce at minimum a `narrative` field so the combined editor can render
 * the narrative section and support phrase highlighting.
 *
 * Add a new converter per source type and register it in normalizeReportContent below.
 */

/**
 * Converts an ASRS formatted string into a structured content object.
 *
 * ASRS content is a pre-formatted text document with sections like:
 *   Date/Time: 2015-07 0001-0600
 *   Narrative (Report 1):
 *   <body text>
 *   Synopsis:
 *   <synopsis text>
 *
 * TODO: This is a temporary client-side workaround. Ideally the ASRS ingestion pipeline
 * (python/reports/asrs/mapper.py) should parse the raw text and store content as a structured
 * JSON object with a top-level `narrative` field, consistent with NTSB reports. Once that is
 * done this converter can be removed.
 */
export function convertAsrsContent(rawContent: string): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    // Report ID from the first line: "ASRS Report: 1274968"
    const reportIdMatch = /^ASRS Report:\s*(\S+)/m.exec(rawContent);
    if (reportIdMatch) result['report_id'] = reportIdMatch[1];

    // Simple top-level inline fields present in every ASRS report
    const TOP_LEVEL_FIELDS: [string, string][] = [
        ['Date/Time', 'date_time'],
        ['Location', 'location'],
        ['Altitude AGL', 'altitude_agl'],
    ];
    for (const [label, key] of TOP_LEVEL_FIELDS) {
        const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const m = new RegExp(`^${escaped}:\\s+(.+)$`, 'm').exec(rawContent);
        if (m) result[key] = m[1].trim();
    }

    // Narrative body: from "Narrative (Report N):\n" to "Synopsis:\n" or end of string
    const narrativeExec = /^Narrative[^:\n]*:/m.exec(rawContent);
    const synopsisExec = /^Synopsis:/m.exec(rawContent);

    if (narrativeExec) {
        const bodyStart = rawContent.indexOf('\n', narrativeExec.index + narrativeExec[0].length) + 1;
        const bodyEnd = synopsisExec ? synopsisExec.index : rawContent.length;
        result['narrative'] = rawContent.slice(bodyStart, bodyEnd).trim();
    }

    // Synopsis body: from "Synopsis:\n" to end of string
    if (synopsisExec) {
        const bodyStart = rawContent.indexOf('\n', synopsisExec.index + synopsisExec[0].length) + 1;
        result['synopsis'] = rawContent.slice(bodyStart).trim();
    }

    return result;
}

/**
 * Dispatches to the appropriate source-specific converter when content arrives as a raw string.
 * Returns content unchanged when it is already a structured object (e.g. NTSB).
 */
export function normalizeReportContent(sourceName: string, content: unknown): unknown {
    if (typeof content !== 'string') return content;

    if (sourceName === 'ASRS') return convertAsrsContent(content);

    // Other string-format sources: add converters here as needed
    return content;
}

export const reportContentConverters: Record<string, (raw: string) => Record<string, unknown>> = {
    ASRS: convertAsrsContent,
};
